import React, { useRef, useEffect, MutableRefObject } from 'react';
import { PitchResult } from '../utils/pitchDetection';
import { harmonicRecipe } from '../utils/timbre';

interface Props {
  spectrumRef: MutableRefObject<Float32Array | null>;
  pitchRef: MutableRefObject<PitchResult | null>;
  sampleRate: number;
  binCount: number;
  harmonics?: number;
}

const PEAK_DECAY_PER_S = 0.7; // how fast the peak-hold ticks fall (fraction of full height per second)

// Bars for the fundamental and its overtones. Reads refs every frame like
// the other canvases; no React state on the hot path. A slow-falling
// peak-hold tick on each bar shows the attack recipe of a plucked note
// while the sustain decays underneath it.
export default function HarmonicRecipe({ spectrumRef, pitchRef, sampleRate, binCount, harmonics = 8 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const peaksRef = useRef<Float32Array>(new Float32Array(harmonics));
  const lastTsRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const draw = (ts: number) => {
      const dpr = window.devicePixelRatio || 1;
      const cw = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const ch = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      const W = cw, H = ch;
      const dt = lastTsRef.current ? Math.min(0.1, (ts - lastTsRef.current) / 1000) : 0;
      lastTsRef.current = ts;

      ctx.clearRect(0, 0, W, H);

      const spectrum = spectrumRef.current;
      const pitch = pitchRef.current;
      const padL = 6 * dpr, padR = 6 * dpr, padT = 16 * dpr, padB = 14 * dpr;
      const plotH = H - padT - padB;
      const slot = (W - padL - padR) / harmonics;
      const barW = slot * 0.62;

      const peaks = peaksRef.current;
      const rec = spectrum && pitch && pitch.hz > 0
        ? harmonicRecipe(spectrum, sampleRate, binCount, pitch.hz, harmonics)
        : null;

      // Baseline
      ctx.strokeStyle = 'rgba(69,124,159,0.6)';
      ctx.lineWidth = 1 * dpr;
      ctx.beginPath(); ctx.moveTo(padL, H - padB + 0.5); ctx.lineTo(W - padR, H - padB + 0.5); ctx.stroke();

      ctx.font = `${9 * dpr}px "Courier New", monospace`;
      ctx.textAlign = 'center';

      for (let i = 0; i < harmonics; i++) {
        const h = rec && rec[i] ? rec[i] : null;
        const rel = h ? h.rel : 0;
        // Peak hold: jump up instantly, fall slowly
        peaks[i] = Math.max(rel, peaks[i] - PEAK_DECAY_PER_S * dt);
        if (!rec) peaks[i] = Math.max(0, peaks[i] - PEAK_DECAY_PER_S * dt);

        const x = padL + i * slot + (slot - barW) / 2;
        const bh = rel * plotH;
        const y = H - padB - bh;

        // Bar
        if (bh > 0) {
          const grad = ctx.createLinearGradient(0, y, 0, H - padB);
          if (i === 0) { grad.addColorStop(0, 'rgba(208,224,122,0.95)'); grad.addColorStop(1, 'rgba(152,165,82,0.55)'); }
          else { grad.addColorStop(0, 'rgba(192,224,243,0.9)'); grad.addColorStop(1, 'rgba(69,124,159,0.45)'); }
          ctx.fillStyle = grad;
          ctx.fillRect(x, y, barW, bh);
        }
        // Peak tick
        const py = H - padB - peaks[i] * plotH;
        if (peaks[i] > 0.02) {
          ctx.fillStyle = i === 0 ? 'rgba(227,185,85,0.95)' : 'rgba(201,160,58,0.8)';
          ctx.fillRect(x - 1 * dpr, py - 1 * dpr, barW + 2 * dpr, 2 * dpr);
        }
        // Label: harmonic number
        ctx.fillStyle = i === 0 ? 'rgba(208,224,122,0.9)' : 'rgba(182,173,196,0.75)';
        ctx.fillText(`${i + 1}×`, x + barW / 2, H - 3 * dpr);
      }

      // Header: what f0 we are slicing at
      ctx.textAlign = 'left';
      ctx.font = `bold ${10 * dpr}px "Courier New", monospace`;
      if (pitch && rec) {
        ctx.fillStyle = 'rgba(250,237,175,0.95)';
        ctx.fillText(`${pitch.note}${pitch.octave}  ${Math.round(pitch.hz)} Hz`, padL, 11 * dpr);
        // Right side: top harmonic frequency so the scale is legible
        const last = rec[rec.length - 1];
        ctx.textAlign = 'right';
        ctx.fillStyle = 'rgba(182,173,196,0.7)';
        ctx.fillText(`${rec.length}× = ${Math.round(last.hz)} Hz`, W - padR, 11 * dpr);
      } else {
        ctx.fillStyle = 'rgba(122,109,140,0.85)';
        ctx.fillText('play or sing a note', padL, 11 * dpr);
      }

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [spectrumRef, pitchRef, sampleRate, binCount, harmonics]);

  return (
    <div className="rail-canvas-wrap">
      <canvas ref={canvasRef} className="rail-canvas" />
    </div>
  );
}
